import type { AnalyticsComparison, AnalyticsPeriod, Lens } from '~/types/leLens';

import type { AnalyticsAreaId } from '../catalogue/analyticsDomains';
import { PAID_CLICK_LABELS } from '../catalogue/growth/metricLabels';
import { isCertifiedLens } from '../lensLibrary';

import { AREA_TABLEAU_SNAPSHOT, type AreaTableauSnapshot } from './areaTableauSnapshot';
import { LOYALTY_HEADLINE_IDS } from './loyaltySplit';

export type AreaId = AnalyticsAreaId;

export const AREA_IDS: readonly AreaId[] = ['commercial', 'growth', 'loyalty', 'supply', 'customer-service'];

export const isAreaId = (value: string | null | undefined): value is AreaId => AREA_IDS.includes(value as AreaId);

/** A fact we do not have yet. The page shows `reason`, never an invented value. */
export type Placeholder<T> = { kind: 'placeholder'; reason: string } | { kind: 'value'; value: T };

export const placeholder = (reason: string): Placeholder<never> => ({ kind: 'placeholder', reason });

/** Home's Business Pulse and every area page read the same window. */
export const AREA_SNAPSHOT_SELECTION: { period: AnalyticsPeriod; comparison: AnalyticsComparison } = {
  period: 'month_to_date',
  comparison: 'previous_year',
};

export type AreaMetrics =
  /**
   * Governed metric ids from the domain registry, in display order. `labels` overrides the server's
   * short name (Growth's figures are paid media only and must say so). `neutral` ids are inputs, such as
   * spend, where up is neither good nor bad.
   */
  | {
      kind: 'governed';
      metricIds: readonly string[];
      drillBreakdown: string | null;
      labels?: Readonly<Record<string, string>>;
      neutral?: readonly string[];
    }
  /**
   * No governed metrics exist. The labels are a proposal for the area lead, or, with `agreed`, metrics already
   * agreed that the reporting build has not connected yet.
   */
  | { kind: 'proposed'; labels: readonly string[]; agreed?: boolean };

/** The note a tile with no figure shows, by whether its metric is only proposed or already agreed. */
export const unconnectedNote = (metrics: AreaMetrics) =>
  metrics.kind === 'proposed' && metrics.agreed ? 'Not connected yet' : 'Not defined';

export type AreaContract = {
  id: AreaId;
  /** Short label for the rail and the page title. The registry names are longer. */
  label: string;
  description: string;
  lead: Placeholder<{ name: string; email: string }>;
  metrics: AreaMetrics;
  askSuggestions: readonly [string, string, string];
  /** How many governed datasets askRichard answers this area from (domain registry datasetIds). */
  governedDatasetCount: number;
  tableau: AreaTableauSnapshot;
  requestReportUrl: Placeholder<string>;
};

const LEAD_TBC = placeholder('to be named');
/** The real route today. Verified public channel, 16 Oct 2026. */
export const DATA_REQUESTS_CHANNEL = '#data-requests';

/** The header line naming the area lead. Nothing while the lead is still a placeholder. */
export function areaLeadMeta(areaId: AreaId): string | undefined {
  const { lead } = AREA_CONTRACTS[areaId];
  return lead.kind === 'value' ? `Area lead: ${lead.value.name}` : undefined;
}

const REQUEST_TBC = placeholder(`Ask in ${DATA_REQUESTS_CHANNEL}. A request form is coming.`);

export const AREA_CONTRACTS: Record<AreaId, AreaContract> = {
  commercial: {
    id: 'commercial',
    label: 'Commercial',
    description: 'Sales, margin, orders and refunds across every product and market.',
    lead: LEAD_TBC,
    metrics: {
      kind: 'governed',
      metricIds: [
        'commercial.ttv_br',
        'commercial.gross_margin_br',
        'commercial.margin_pct_br',
        'commercial.orders',
        'commercial.refunds_pct_gm',
      ],
      drillBreakdown: 'product',
      labels: {
        'commercial.gross_margin_br': 'Gross margin (BR)',
        'commercial.margin_pct_br': 'Margin % (BR)',
        'commercial.orders': 'Orders',
        'commercial.refunds_pct_gm': 'Refunds % of GM',
        'commercial.ttv_br': 'TTV (BR)',
      },
    },
    askSuggestions: [
      'Which products moved TTV most this month?',
      'Which markets grew Bedbank margin?',
      'How did margin % move by product?',
    ],
    governedDatasetCount: 7,
    tableau: AREA_TABLEAU_SNAPSHOT.commercial,
    requestReportUrl: REQUEST_TBC,
  },
  growth: {
    id: 'growth',
    label: 'Growth',
    description: 'Paid demand, acquisition and conversion.',
    lead: LEAD_TBC,
    metrics: {
      kind: 'governed',
      metricIds: ['growth.spend', 'growth.margin_br', 'growth.mroas_br', 'growth.sessions', 'growth.cvr'],
      drillBreakdown: 'channel',
      labels: {
        ...PAID_CLICK_LABELS,
        'growth.margin_br': 'Paid GM (BR)',
        'growth.mroas_br': 'mROAS (BR)',
        'growth.sessions': 'Attributed sessions',
        'growth.spend': 'Paid media spend',
      },
      neutral: ['growth.spend'],
    },
    askSuggestions: [
      'Why did GHA mROAS move this month?',
      'Which channels grew paid GM?',
      'How did conversion move by platform?',
    ],
    governedDatasetCount: 14,
    tableau: AREA_TABLEAU_SNAPSHOT.growth,
    requestReportUrl: REQUEST_TBC,
  },
  loyalty: {
    id: 'loyalty',
    label: 'Loyalty',
    description: 'LuxPlus+ membership and Société points.',
    lead: LEAD_TBC,
    // Home and the Loyalty page draw these as two halves, LuxPlus+ then Société: see loyaltySplit.ts.
    metrics: {
      kind: 'governed',
      metricIds: LOYALTY_HEADLINE_IDS,
      drillBreakdown: null,
    },
    askSuggestions: [
      'How many LuxPlus members renewed last month?',
      'Which tiers earn the most points?',
      'How many new members joined this month?',
    ],
    governedDatasetCount: 1,
    tableau: AREA_TABLEAU_SNAPSHOT.loyalty,
    requestReportUrl: REQUEST_TBC,
  },
  supply: {
    id: 'supply',
    label: 'Supply',
    description: 'Live hotel offers: LTLEs, LPCs, LPPs and Tier 1 deals, against the same date last year.',
    lead: LEAD_TBC,
    /*
     * Agreed with Ming Chong, 21 Oct 2026. The figures come from svc-reporting's Supply snapshot
     * (.../analytics/domains/supply/snapshot, see supplyTiles.ts and SupplyAreaPage.tsx), read once the reporting
     * service lists Supply with the hotel_supply dataset; until then these labels show as not connected.
     * Each is a count on the latest completed day against the same date last year, not the Business Pulse period. All four come from lux-analytics-prod.analytics.search_hotel_performance
     * (record_date x offer_bk, history from 1 Sep 2025, so a year-earlier date before 1 Sep 2026 has no figure),
     * COUNT(DISTINCT offer_bk) WHERE is_live_flag, by accommodation_type. Figures for 20 Oct 2026 against 20 Oct 2025:
     * - Live LTLEs: Flash, 452 against 350. is_live_flag includes LuxPlus+ early access.
     * - Live LPCs: LPC, 3,344 against 3,132.
     * - Live LPPs: LPP, 753,726 against 432,975. This is content-approved bedbank properties, the search pool,
     *   not a check of room availability.
     * - Live Tier 1 LTLEs: Flash with deal_tier Luxury Escapes Tier 1 only, 149 against 97. The value is spelt
     *   with an en dash ('Luxury Escapes – Tier 1') and sometimes a hyphen ('Luxury Escapes - Tier 1'), so match
     *   both. Lifestyle Brands Tier 1 (8 more) is left out. 45 live LTLEs had no deal_tier on 20 Oct 2026, 3 a year
     *   earlier.
     */
    metrics: {
      kind: 'proposed',
      agreed: true,
      labels: ['Live LTLE offers', 'Live LPC offers', 'Live LPP offers', 'Live Tier 1 LTLE offers'],
    },
    askSuggestions: [
      'Which Bedbank markets have the least supply?',
      'Which offers get views but no orders?',
      'Where are we short of rooms next month?',
    ],
    governedDatasetCount: 3,
    tableau: AREA_TABLEAU_SNAPSHOT.supply,
    requestReportUrl: REQUEST_TBC,
  },
  'customer-service': {
    id: 'customer-service',
    label: 'Customer Service',
    description: 'Contacts, service outcomes and phone sales.',
    lead: LEAD_TBC,
    metrics: { kind: 'proposed', labels: ['Contacts', 'Contacts per 100 orders', 'CSAT', 'Phone sales TTV'] },
    askSuggestions: [
      'What are the top contact reasons this month?',
      'How many contacts per 100 orders by product?',
      'Which conversations ended in a sale?',
    ],
    governedDatasetCount: 1,
    tableau: AREA_TABLEAU_SNAPSHOT['customer-service'],
    requestReportUrl: REQUEST_TBC,
  },
};

export type ExecutiveReport = {
  title: string;
  areaId: AreaId;
  summary: string;
  /** The certified Lens that holds it. A placeholder until the rebuild lands. */
  lensId: Placeholder<string>;
  /** Where readers go today while the Lens rebuild is in progress. */
  tableauUrl: Placeholder<string>;
};

/** The top layer Jake asked for: about five reports, one more each sprint. Jake named these two. */
export const EXECUTIVE_REPORTS: readonly ExecutiveReport[] = [
  {
    title: 'Key Tracking Report',
    areaId: 'commercial',
    summary: 'Daily trading: TTV, margin and orders',
    lensId: placeholder('Lens rebuild in progress'),
    // Tableau Cloud workbook 1158441, project Core Dashboards (le-data Tableau harvest, 25 Aug 2026).
    tableauUrl: { kind: 'value', value: 'https://10ay.online.tableau.com/#/site/luxuryescapescomau/workbooks/1158441' },
  },
  {
    title: 'LuxPlus',
    areaId: 'loyalty',
    summary: 'Members, renewals and fees',
    lensId: placeholder('Not rebuilt in Lens yet'),
    // Tableau Cloud workbook 1940516 "LuxPlus+", project 1 - Internal + Board (le-data lineage, 21 Oct 2026).
    tableauUrl: { kind: 'value', value: 'https://10ay.online.tableau.com/#/site/luxuryescapescomau/workbooks/1940516' },
  },
];

/** Slots on the executive row still to be chosen with Jake, about one a sprint. */
export const EXECUTIVE_REPORTS_TO_CHOOSE = 3;

/** A certified Lens built on at least one of these governed datasets. */
export function isCertifiedOnDatasets(lens: Lens, governedDatasetIds: ReadonlySet<string>): boolean {
  return (
    isCertifiedLens(lens) &&
    Boolean(
      lens.spec.datasets?.some(
        (dataset) => dataset.source?.kind === 'governed' && governedDatasetIds.has(dataset.source.catalogueId ?? ''),
      ),
    )
  );
}

/**
 * Governed datasets that belong to this area alone. A dataset two areas share (offer_views, traffic) says
 * nothing about which area a Lens on it belongs to, so it never places a Lens.
 */
export function areaOwnDatasetIds(
  domains: ReadonlyArray<{ id: string; datasetIds?: readonly string[] }>,
  areaId: AreaId,
): string[] {
  const elsewhere = new Set(
    domains.filter((domain) => domain.id !== areaId).flatMap((domain) => domain.datasetIds ?? []),
  );
  const own = domains.find((domain) => domain.id === areaId)?.datasetIds ?? [];
  return own.filter((id) => !elsewhere.has(id));
}

/**
 * Certified Lenses for an area. The registry's coreLensIds wins when the server fills it. Until then a
 * certified Lens belongs to the area whose own datasets (areaOwnDatasetIds) it reads; a Lens built only on
 * shared datasets waits for coreLensIds.
 */
export function certifiedLensesForArea(
  lenses: readonly Lens[],
  coreLensIds: readonly string[] = [],
  governedDatasetIds: readonly string[] = [],
): Lens[] {
  if (coreLensIds.length > 0) {
    const byId = new Map(lenses.map((lens) => [lens.id, lens]));
    return coreLensIds.map((id) => byId.get(id)).filter((lens): lens is Lens => Boolean(lens));
  }
  const ids = new Set(governedDatasetIds);
  return lenses.filter((lens) => isCertifiedOnDatasets(lens, ids));
}

/** Callbacks the shell (index.tsx) passes down. One name per action across the rail, Home and area pages. */
export type AreaNavigation = {
  /** Opens view=area&area=<id>, and opens the drill panel when metricId is given. */
  onOpenArea: (areaId: AreaId, metricId?: string) => void;
  /** Opens the existing full analysis page (view=catalogue&domain=<id>). */
  onOpenAreaAnalysis: (areaId: AreaId) => void;
};

export type AreaPageProps = AreaNavigation & {
  areaId: AreaId;
  /** Drill panel open on this metric, from the URL. */
  metricId: string | null;
  onSelectMetric: (metricId: string | null) => void;
  lenses: readonly Lens[];
  lensesStatus: 'loading' | 'error' | 'success';
  onOpenLens: (lensId: string) => void;
  onAskArea: (areaId: AreaId, question: string, context?: string) => void;
  onOpenLibrary: () => void;
  onOpenHome: () => void;
  onNewLens: () => void;
};
